import { useEffect } from 'react';
import { requestReload } from './reloadGate';

/**
 * The build version compiled into this bundle. Injected by the Vite version plugin
 * (see `frontend/shared/src/versionPlugin.ts`) as a global string replacement at build
 * time. Undefined under jest/ts-jest (no define step), which the watcher treats as "dev".
 */
declare const __APP_VERSION__: string | undefined;

const BUILD_VERSION: string =
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

interface VersionWatcherOptions {
  /** Path to the deploy-stamped manifest. Default '/version.json'. */
  url?: string;
  /** Poll interval in ms. Default 60s. */
  intervalMs?: number;
  /** Disable entirely (e.g. for tests). Default true. */
  enabled?: boolean;
}

/**
 * Read the deployed version from the manifest, defending against the SPA fallback:
 * CloudFront serves `index.html` (HTML, 200) for any missing path, so we must NOT treat
 * an HTML body as a version. Returns null on any fetch/parse problem so the caller skips
 * the tick rather than reloading on a bad read.
 */
async function fetchRemoteVersion(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;
    const text = await res.text();
    if (/^\s*</.test(text)) return null; // looks like HTML (SPA fallback) — ignore
    const data = JSON.parse(text) as { version?: unknown };
    return typeof data.version === 'string' ? data.version : null;
  } catch {
    return null;
  }
}

/**
 * Detect a new deploy and refresh the client to it. Polls `/version.json` on an interval
 * and on visibility/focus/online (so a long-open tab updates as soon as the user returns),
 * and on a real version mismatch routes through `requestReload`, which reloads immediately
 * when idle and defers until any in-flight upload finishes.
 *
 * Disabled locally where the build version is unknown ('dev'): there is nothing to compare.
 */
export function useVersionWatcher(options: VersionWatcherOptions = {}): void {
  const { url = '/version.json', intervalMs = 60_000, enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;
    // Skip locally: jest leaves __APP_VERSION__ undefined ('dev'), and non-CI builds /
    // `vite dev` stamp 'dev-<timestamp>'. Either way there's no real deploy to compare to.
    if (!BUILD_VERSION || BUILD_VERSION.startsWith('dev')) return;

    let cancelled = false;

    const check = async () => {
      const remote = await fetchRemoteVersion(url);
      if (cancelled || !remote) return;
      if (remote !== BUILD_VERSION) {
        requestReload('new_version');
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const onCheck = () => void check();

    const interval = setInterval(onCheck, intervalMs);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onCheck);
    window.addEventListener('online', onCheck);
    void check();

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onCheck);
      window.removeEventListener('online', onCheck);
    };
  }, [url, intervalMs, enabled]);
}
